import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import DragIndicatorRoundedIcon from '@mui/icons-material/DragIndicatorRounded';
import { Box, Chip, IconButton, Stack, Switch, Typography } from '@mui/material';
import React from 'react';
import { BLUE, BORDER, INTER, MUTED, NAVY, POPPINS, SLATE, SOFT_BLUE, SURFACE_TINT } from '../teacherStyles';
import { lessons } from './ClassPageData';
import { SectionCard } from './ClassPageUi';

interface ClassLessonsPageProps {
    onOpenLesson: (lessonId: string) => void;
    onAction: (message: string) => void;
}

export default function ClassLessonsPage({ onOpenLesson, onAction }: ClassLessonsPageProps) {
    const [classLessons, setClassLessons] = React.useState(lessons);
    const [draggedLessonId, setDraggedLessonId] = React.useState<string | null>(null);
    const [dragOverLessonId, setDragOverLessonId] = React.useState<string | null>(null);

    const startDragging = (event: React.DragEvent, lessonId: string) => {
        event.dataTransfer.effectAllowed = 'move';
        event.dataTransfer.setData('text/plain', String(lessonId));
        setDraggedLessonId(lessonId);
    };

    const dropLesson = (targetLessonId: string) => {
        if (draggedLessonId === null || draggedLessonId === targetLessonId) {
            setDraggedLessonId(null);
            setDragOverLessonId(null);
            return;
        }

        setClassLessons(current => {
            const fromIndex = current.findIndex(lesson => lesson.id === draggedLessonId);
            const toIndex = current.findIndex(lesson => lesson.id === targetLessonId);
            if (fromIndex < 0 || toIndex < 0) return current;

            const reordered = [...current];
            const [movedLesson] = reordered.splice(fromIndex, 1);
            reordered.splice(toIndex, 0, movedLesson);
            return reordered;
        });
        setDraggedLessonId(null);
        setDragOverLessonId(null);
        onAction('Lesson order updated in this static demo.');
    };

    const setAvailable = (lessonId: string, available: boolean) => {
        setClassLessons(current => current.map(lesson => lesson.id === lessonId ? { ...lesson, available } : lesson));
        onAction(available ? 'Lesson is now available to students.' : 'Lesson is now hidden from students.');
    };

    return (
        <SectionCard sx={{ overflow: 'hidden' }}>
            <Box sx={{ p: 2.5, borderBottom: `1px solid ${BORDER}` }}>
                <Typography sx={{ fontFamily: POPPINS, fontWeight: 600, fontSize: 18, color: NAVY }}>Class lessons</Typography>
            </Box>
            <Stack spacing={1.5} sx={{ p: 2.5 }}>
                {classLessons.map((lesson, index) => (
                    <Box
                        key={lesson.id}
                        onDragOver={event => {
                            event.preventDefault();
                            event.dataTransfer.dropEffect = 'move';
                            setDragOverLessonId(lesson.id);
                        }}
                        onDrop={event => {
                            event.preventDefault();
                            dropLesson(lesson.id);
                        }}
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '26px 44px minmax(0, 1fr) 38px', md: '26px 48px minmax(0, 1fr) 105px 185px 38px' },
                            gap: 1.25,
                            alignItems: 'center',
                            p: 1.75,
                            border: `1px solid ${dragOverLessonId === lesson.id && draggedLessonId !== lesson.id ? BLUE : BORDER}`,
                            borderRadius: '15px',
                            backgroundColor: lesson.available ? '#fff' : SURFACE_TINT,
                            opacity: draggedLessonId === lesson.id ? 0.55 : 1,
                            transition: 'border-color 120ms ease, opacity 120ms ease',
                        }}
                    >
                        <Box
                            draggable
                            onDragStart={event => startDragging(event, lesson.id)}
                            onDragEnd={() => { setDraggedLessonId(null); setDragOverLessonId(null); }}
                            role="button"
                            tabIndex={0}
                            aria-label={`Drag to reorder ${lesson.title}`}
                            sx={{ display: 'grid', placeItems: 'center', alignSelf: 'stretch', borderRadius: '9px', color: MUTED, cursor: 'grab', '&:hover': { color: BLUE, backgroundColor: SOFT_BLUE }, '&:active': { cursor: 'grabbing' } }}
                        >
                            <DragIndicatorRoundedIcon sx={{ fontSize: 21 }} />
                        </Box>
                        <Box sx={{ width: 42, height: 42, borderRadius: '13px', display: 'grid', placeItems: 'center', backgroundColor: `${lesson.color}12`, color: lesson.textColor, fontFamily: POPPINS, fontWeight: 700 }}>{index + 1}</Box>
                        <Box>
                            <Typography sx={{ fontFamily: POPPINS, fontWeight: 600, fontSize: 13.5, color: NAVY }}>{lesson.title}</Typography>
                            {lesson.detail && <Typography sx={{ fontFamily: INTER, fontSize: 11.5, color: SLATE, mt: 0.25 }}>{lesson.detail}</Typography>}
                        </Box>
                        <Chip label={lesson.status} size="small" sx={{ display: { xs: 'none', md: 'flex' }, justifySelf: 'start', fontFamily: INTER, fontWeight: 700, fontSize: 11, color: lesson.textColor, backgroundColor: `${lesson.color}12` }} />
                        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ display: { xs: 'none', md: 'flex' } }}>
                            <Switch size="small" checked={lesson.available} onChange={event => setAvailable(lesson.id, event.target.checked)} inputProps={{ 'aria-label': `${lesson.title} available to students` }} />
                            <Box>
                                <Typography sx={{ fontFamily: INTER, fontWeight: 700, fontSize: 11.5, color: lesson.available ? NAVY : SLATE }}>Available to students</Typography>
                                <Typography sx={{ fontFamily: INTER, fontSize: 11, color: SLATE, mt: 0.1 }}>{lesson.available ? `${lesson.completion} students completed` : 'Hidden'}</Typography>
                            </Box>
                        </Stack>
                        <IconButton size="small" aria-label={`Open ${lesson.title}`} onClick={() => onOpenLesson(lesson.id)} sx={{ color: BLUE }}><ChevronRightRoundedIcon /></IconButton>
                        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ display: { xs: 'flex', md: 'none' }, gridColumn: '3 / -1', mt: 0.25 }}>
                            <Switch size="small" checked={lesson.available} onChange={event => setAvailable(lesson.id, event.target.checked)} inputProps={{ 'aria-label': `${lesson.title} available to students on mobile` }} />
                            <Typography sx={{ fontFamily: INTER, fontWeight: 700, fontSize: 11.5, color: lesson.available ? NAVY : SLATE }}>{lesson.available ? 'Available to students' : 'Hidden from students'}</Typography>
                        </Stack>
                    </Box>
                ))}
            </Stack>
        </SectionCard>
    );
}
